import { auth } from '../lib/firebase';
import type {
  CiqualMatchCandidate,
  OffMatchCandidate,
  IngredientNutrition,
} from '../types/nutrition';
import { normalizeBackendUrl } from './backendUrl';

const BACKEND_URL = normalizeBackendUrl(import.meta.env.VITE_BACKEND_URL);

async function getIdToken(): Promise<string> {
  const user = auth.currentUser;
  if (!user) throw new Error('Vous devez être connecté en admin.');
  return user.getIdToken();
}

async function apiFetch<T>(
  path: string,
  init: { method?: string; body?: BodyInit | null; headers?: Record<string, string> } = {}
): Promise<T> {
  const token = await getIdToken();
  const response = await fetch(`${BACKEND_URL}${path}`, {
    method: init.method || 'GET',
    headers: {
      Authorization: `Bearer ${token}`,
      ...(init.body && !(init.body instanceof FormData)
        ? { 'Content-Type': 'application/json' }
        : {}),
      ...init.headers,
    },
    body: init.body,
  });

  const payload = await response.json().catch(() => ({}));
  if (!response.ok) {
    throw new Error((payload as any)?.error || `Request failed (${response.status})`);
  }
  return payload as T;
}

export interface CiqualImportPreview {
  sourceVersion: string;
  sheetName: string;
  totalParsed: number;
  skippedInfant: number;
  added: number;
  modified: number;
  unchanged: number;
  removed: number;
}

export interface CiqualImportReport {
  sourceVersion: string;
  totalParsed: number;
  skippedInfant: number;
  written: number;
  added: number;
  updated: number;
  deleted: number;
}

export function getCiqualMeta() {
  return apiFetch<{ success: boolean; meta: any }>('/api/nutrition/ciqual/meta');
}

export async function previewCiqualImport(file: File, sourceVersion: string) {
  const form = new FormData();
  form.append('file', file);
  form.append('sourceVersion', sourceVersion);
  return apiFetch<{ success: boolean; preview: CiqualImportPreview }>(
    '/api/nutrition/ciqual/preview',
    { method: 'POST', body: form }
  );
}

export async function commitCiqualImport(
  file: File,
  sourceVersion: string,
  purgeMissing: boolean
) {
  const form = new FormData();
  form.append('file', file);
  form.append('sourceVersion', sourceVersion);
  form.append('purgeMissing', String(purgeMissing));
  return apiFetch<{ success: boolean; report: CiqualImportReport }>(
    '/api/nutrition/ciqual/import',
    { method: 'POST', body: form }
  );
}

export function searchCiqual(query: string, limit = 15) {
  return apiFetch<{ success: boolean; candidates: CiqualMatchCandidate[] }>(
    '/api/nutrition/ciqual/search',
    { method: 'POST', body: JSON.stringify({ query, limit }) }
  );
}

export function searchOff(query: string, limit = 5) {
  return apiFetch<{ success: boolean; candidates: OffMatchCandidate[] }>(
    '/api/nutrition/off/search',
    { method: 'POST', body: JSON.stringify({ query, limit }) }
  );
}

export function previewMassSync() {
  return apiFetch<{ success: boolean; preview: Record<string, number | string | null> }>(
    '/api/nutrition/mass-sync/preview',
    { method: 'POST', body: JSON.stringify({}) }
  );
}

export function runMassSync() {
  return apiFetch<{ success: boolean; report: Record<string, unknown> }>(
    '/api/nutrition/mass-sync/run',
    { method: 'POST', body: JSON.stringify({}) }
  );
}

export type MatchConfidence = 'high' | 'medium' | 'low' | 'none';

export interface BulkMatchOptions {
  highMinScore?: number;
  highGap?: number;
  mediumMinScore?: number;
  language?: 'fr' | 'en' | null;
  limit?: number | null;
}

export interface BulkMatchPreview {
  scannedEligible: number;
  skippedAlreadyMatched: number;
  byConfidence: Record<MatchConfidence, number>;
  thresholds: { highMinScore: number; highGap: number; mediumMinScore: number };
}

export interface BulkMatchCandidateOption {
  ciqualCode: number;
  nameRaw: string;
  score: number;
  preparationState?: string | null;
}

export interface BulkMatchReviewItem {
  id: string;
  ingredientId: string;
  ingredientName: string;
  language: string | null;
  confidence: MatchConfidence;
  bestScore: number | null;
  suggestedCiqualCode: number | null;
  candidates: BulkMatchCandidateOption[];
  status: 'pending' | 'applied' | 'skipped';
}

export interface BulkMatchReport {
  runId?: string;
  status: string;
  processed: number;
  queued: number;
  skipped: number;
  byConfidence?: Record<MatchConfidence, number>;
}

export interface BulkMatchRun {
  id: string;
  status: string;
  processed?: number;
  queued?: number;
  skipped?: number;
  byConfidence?: Record<MatchConfidence, number>;
  startedAt?: unknown;
  finishedAt?: unknown;
}

export function previewBulkMatch(options: BulkMatchOptions = {}) {
  return apiFetch<{ success: boolean; preview: BulkMatchPreview }>(
    '/api/nutrition/bulk-match/preview',
    { method: 'POST', body: JSON.stringify(options) }
  );
}

export function runBulkMatch(options: BulkMatchOptions = {}) {
  return apiFetch<{ success: boolean; report: BulkMatchReport }>(
    '/api/nutrition/bulk-match/run',
    { method: 'POST', body: JSON.stringify(options) }
  );
}

export function getLatestBulkMatchRun() {
  return apiFetch<{ success: boolean; run: BulkMatchRun }>(
    '/api/nutrition/bulk-match/runs/latest'
  );
}

export function getBulkMatchRun(runId: string) {
  return apiFetch<{ success: boolean; run: BulkMatchRun }>(
    `/api/nutrition/bulk-match/runs/${encodeURIComponent(runId)}`
  );
}

export function listBulkMatchItems(
  runId: string,
  opts: { confidence?: MatchConfidence | ''; status?: 'pending' | 'applied' | 'skipped' | 'all' } = {}
) {
  const params = new URLSearchParams();
  if (opts.confidence) params.set('confidence', opts.confidence);
  if (opts.status) params.set('status', opts.status);
  const qs = params.toString();
  return apiFetch<{ success: boolean; items: BulkMatchReviewItem[] }>(
    `/api/nutrition/bulk-match/runs/${encodeURIComponent(runId)}/items${qs ? `?${qs}` : ''}`
  );
}

export function applyBulkMatchReview(ingredientId: string, ciqualCode: number, runId?: string) {
  return apiFetch<{ success: boolean }>('/api/nutrition/bulk-match/apply-review', {
    method: 'POST',
    body: JSON.stringify({ ingredientId, ciqualCode, runId }),
  });
}

export function skipBulkMatchItem(runId: string, ingredientId: string) {
  return apiFetch<{ success: boolean }>('/api/nutrition/bulk-match/skip', {
    method: 'POST',
    body: JSON.stringify({ runId, ingredientId }),
  });
}

export function applyBulkMatchByConfidence(runId: string, confidence: MatchConfidence) {
  return apiFetch<{ success: boolean; applied: number; failed: number }>(
    '/api/nutrition/bulk-match/apply-confidence',
    { method: 'POST', body: JSON.stringify({ runId, confidence }) }
  );
}

export function applyNutrition(ingredientId: string, nutrition: IngredientNutrition) {
  return apiFetch<{ success: boolean }>('/api/nutrition/apply', {
    method: 'POST',
    body: JSON.stringify({ ingredientId, nutrition }),
  });
}
